import XLSX from 'xlsx';
import moment from 'moment';
import invariant from 'invariant';
import { isNumber } from 'util';
import qs from 'querystring';

import { server } from '../assets/lang.json';
import { isMC } from './env';

/**
 * 事件跟踪
 * @param {string} category	必填；表示事件发生在谁身上，如“视频”、“小说”、“轮显层”等等
 * @param {string} action	必填；表示访客跟元素交互的行为动作，如"播放"、"收藏"、"翻层"等等。
 * @param {string} label	选填；用于更详细的描述事件，如具体是哪个视频，哪部小说。
 * @param {number} value	选填；用于填写打分型事件的分值，加载时间型事件的时长，订单型事件的价格。
 *                        请填写整数数值，如果填写为其他形式，系统将按0处理。若填写为浮点小数，系统会自动取整，去掉小数点。
 * @param {string} nodeId	选填	string	填写事件元素的div元素id。	请填写class id，暂不支持name。
 */
export function czcPush(category, action, label, value, nodeId) {
  window._czc && window._czc.push(['_trackEvent', category, action, label, value, nodeId]);
}
/**
 * @param {React.ComponentType<any>} Cmp
 */
export function getDisplayName(Cmp) {
  return Cmp.displayName || Cmp.name || 'Component';
}
/**
 * 金额转化：带 ¥ 、千位分隔符且保留两位小数
 * @param {string | number} money 金额
 */
export const yuan = (money = 0) => {
  invariant(!isNaN(Number(money)), '金额格式错误');
  return (
    symbols() +
    Number(money)
      .toFixed(2)
      .replace(/(^|\s)\d+/g, (m) => m.replace(/(?=(?!\b)(\d{3})+$)/g, ','))
  );
};
/**
 * 多客与mc的货币符号，mc不显示货币符号，多客显示￥
 */
export const symbols = (() => {
  if (!isMC()) {
    return '\xA5 ';
  } else {
    return '';
  }
})();
/**
 * 前端导出 Excel 表格
 *
 * @param {array} config 列标题和所展示对应字段
 * @param {array} data 数据列表
 * @param {string} title 导出 Excel 的文件名
 * @param {string} label tab名，默认为文件名
 * @param {function} cb 回调函数
 */
export function exportToExcel(config, data = [], title = '', label, cb) {
  invariant(!!config && !!config.length && !!config[0].length, '导出配置项格式错误');
  const source = [
    config.map((v) => v[0]),
    ...data.map((row) => config.map((v) => (!v[2] ? row[v[1]] : v[2](row[v[1]])))),
  ];

  const tabName = typeof label === 'string' ? label : title;
  const ws = XLSX.utils.aoa_to_sheet(source, {});
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, tabName);
  XLSX.writeFile(wb, title + moment().format('L') + '.xlsx');
  cb && typeof cb === 'function' ? cb() : label && typeof label === 'function' && label();
}
/**
 * 后端导出excel表格
 *
 * @export
 * @param {*} res
 * @param {string} [title='未命名']
 */
export function exportToExcelFromNet(res, title = '') {
  let blob = new Blob([res], { type: 'application/vnd.ms-excel' });
  let link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.location = window.URL.createObjectURL(blob);
  link.download = `${title}${moment().format('L')}.xlsx`;
  link.style.display = 'none';
  document.body.appendChild(link); // 兼容firefox
  link.click();
  link.remove();
}
/**
 * 解析导入的excel数据
 *
 * @export
 * @param {*} file
 * @returns
 */

export function parseExcel(file) {
  invariant(file, 'miss file');
  return new Promise((resolve, reject) => {
    // 读取文件
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = (e) => {
      try {
        const bstr = e.target.result;
        const uint = new Uint8Array(bstr);
        if (!checkString(uint)) {
          //兼容mac wps
          for (let i = 8; i < 24; i++) {
            uint[i] = 0;
          }
        }
        // const wb = XLSX.read(uint, { type: 'array' });
        const wb = XLSX.read(uint, { type: 'array', cellDates: true });
        const wsName = wb.SheetNames[0];
        const ws = wb.Sheets[wsName];
        let data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        data = transExcel(data);
        resolve(data);
      } catch (error) {
        reject(error);
        throw error;
      }
    };
  });
}

/**
 * 转换excel数据
 *
 * @export
 * @param {*} data
 * @returns
 */
export function transExcel(data) {
  try {
    // 为避免改变源数据，则深拷贝一份
    let [...excelData] = data;
    // 表格第一行是否为注意事项,如果是的话，则忽略第一行
    const isNotesOfExcelLineOne = excelData[0].length === 1;
    isNotesOfExcelLineOne && excelData.shift();
    // 根据第一行补齐其他所有行
    const headerRow = excelData[0].map((h) => h.toString());
    const len = headerRow.length;
    // 迭代 empty 语句为 undefined
    const idx = excelData.findIndex((v) => !v || !v.length);
    excelData = (idx > -1 ? excelData.splice(0, idx) : excelData).map((row) => {
      row.length = len;
      return headerRow.map((h, idx) =>
        row[idx] && isNumber(row[idx])
          ? String(Math.round(row[idx] * 10000) / 10000)
          : row[idx]?.toString() || ''
      );
    });
    return excelData;
  } catch (error) {
    throw error;
  }
}
/**
 * 检查传入的 CLS ID 是否是office
 * @param {*} b
 * @param {*} s
 * @param {*} l
 *
 */
function checkString(b) {
  const ss = [];
  for (let i = 8; i < 24; ++i) ss.push(b[i].toString(16).slice(-2));
  return ss.join('') === '0000000000000000';
}

const envMap = {
  development: 'develop',
  prod: 'production',
  testing: 'test',
};

export const getEnv = () => {
  const search = (window.location.search || '?').replace(/[/\\]/g, '');
  const query = qs.parse(search.slice(1));
  let env = process.env.API_ENV || process.env.NODE_ENV;
  env = envMap[env] || env;
  return env !== 'production' ? envMap[query.env] || query.env || env : env;
};
/**
 * 获取页面地址
 *
 * @param {string} key 域名名称，默认 origin
 */
export const getBaseUrl = (key = 'origin', env) => {
  // 当前环境下所有可调用的地址
  const urls = server[env || getEnv()] || {};
  return urls[key] || 'https://api.duoke.net/index.php';
};
export const isProd = () => getEnv() === 'production';
export const isProdTest = () =>
  getEnv() === 'production' && window.location.href.includes('air-test.duoke.net');

export const randomString = () => {
  return (Math.random() * Date.now() * 10000).toString(16);
};
